import { configureRequestParams, getErrorMessage } from '@/utils/helpers'

import axios from './instance'

/**
 * Resolve the base path for a Certificate request.
 *
 * - With organizationId → /organizations/<organizationId>/certificates/ (the
 *   org's own shared certificates — RSCX-320)
 * - With entityId → /entities/<entityId>/certificates/ (returns the entity's
 *   own facility certificates + global RescueX + org-shared certificates)
 * - With neither → /certificates/ (RescueX-only)
 */
const basePath = (entityId, organizationId) => {
  if (organizationId) return `/organizations/${organizationId}/certificates/`
  if (entityId) return `/entities/${entityId}/certificates/`
  return '/certificates/'
}

const LIST_EXPAND =
  'expand=course_requirements.primary_course,course_requirements.alternative_courses,training'
const DETAIL_EXPAND =
  'expand=course_requirements.primary_course,course_requirements.alternative_courses,proficiency_questions,course_descriptions,instructor_assets'
const UPDATE_EXPAND =
  'expand=course_requirements.primary_course,course_requirements.alternative_courses,proficiency_questions,course_descriptions'

/**
 * Gets the list of certificates.
 */
export const getCertificates = async ({
  entityId,
  organizationId,
  sorting,
  pagination,
  searchTerm,
  filters,
} = {}) => {
  const params = configureRequestParams(sorting, pagination, searchTerm, filters)
  try {
    return await axios.get(`${basePath(entityId, organizationId)}${params}&${LIST_EXPAND}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Add a new certificate.
 */
export const addCertificate = async ({ entityId, organizationId, ...payload } = {}) => {
  try {
    return await axios.post(basePath(entityId, organizationId), payload)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Get a certificate.
 */
export const getCertificate = async ({ entityId, organizationId, id } = {}) => {
  try {
    return await axios.get(`${basePath(entityId, organizationId)}${id}?${DETAIL_EXPAND}`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Update a certificate.
 */
export const updateCertificate = async ({ entityId, organizationId, ...payload }) => {
  try {
    return await axios.patch(
      `${basePath(entityId, organizationId)}${payload.id}/?${UPDATE_EXPAND}`,
      payload,
    )
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}

/**
 * Delete a certificate.
 */
export const deleteCertificate = async ({ entityId, organizationId, id } = {}) => {
  try {
    return await axios.delete(`${basePath(entityId, organizationId)}${id}/`)
  } catch (err) {
    throw new Error(getErrorMessage(err))
  }
}
